import './bootLoading.css';

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export type BootStep = {
  key: string;
  label: string;
  done: boolean;
};

// The one screen shown before the presentation. It is not cosmetic: the session, both
// websockets and the microphone all come up behind it, and none of them is allowed to
// reach the customer until every step has landed.
//
// The step labels stay deliberately vague. The customer needs to see that something is
// happening and that it is progressing — naming the services being reached would tell
// them nothing they can act on.
export default function BootLoading({
  steps,
  progress,
  error,
  onRetry
}: {
  steps: BootStep[];
  progress: number;
  error?: { message: string; retryable: boolean } | null;
  onRetry?: () => void;
}) {
  // Only the first unfinished step spins; the ones behind it stay dimmed, so the eye
  // lands on what is actually in flight.
  const activeIndex = steps.findIndex((step) => !step.done);

  return (
    <main className="pvl-page">
      <div className="pvl-glow" aria-hidden="true" />

      <section className="pvl-card" role="status" aria-live="polite">
        <div className="pvl-orb" aria-hidden="true">
          <span className="pvl-orb-ring pvl-orb-ring--1" />
          <span className="pvl-orb-ring pvl-orb-ring--2" />
          <span className="pvl-orb-core" />
        </div>

        <h1>Getting your presentation ready</h1>
        <p>{error ? 'We hit a snag getting started.' : 'This only takes a moment.'}</p>

        <div className="pvl-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <i style={{ width: `${Math.max(4, progress)}%` }} />
        </div>
        <span className="pvl-percent">{progress}%</span>

        <ul className="pvl-steps">
          {steps.map((step, index) => {
            const isActive = index === activeIndex;
            const isFailed = isActive && !!error;
            return (
              <li
                key={step.key}
                className={`pvl-step${step.done ? ' is-done' : ''}${isActive && !isFailed ? ' is-active' : ''}${
                  isFailed ? ' is-failed' : ''
                }`}
              >
                <span className="pvl-step-mark" aria-hidden="true">
                  {step.done ? <IconCheck /> : isFailed ? '!' : <i className="pvl-step-spinner" />}
                </span>
                <span className="pvl-step-label">{step.label}</span>
              </li>
            );
          })}
        </ul>

        {error ? (
          <div className="pvl-error">
            <p>{error.message}</p>
            {error.retryable && onRetry && (
              <button type="button" className="pvl-retry" onClick={onRetry}>
                Try again
              </button>
            )}
          </div>
        ) : (
          <p className="pvl-hint">This happens once — your next visit starts straight away.</p>
        )}
      </section>

      <footer className="pvl-footer">
        <span className="pvl-mark">NUWAY</span>
        <span>AI presenter</span>
      </footer>
    </main>
  );
}
